import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;

function EmployeeSelfService({ user, onLogout }) {
  const [profile, setProfile] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    fetchProfile();
    fetchOnboarding();
    fetchDocuments();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employee-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      setFormData({
        personal_email: response.data.personal_email || '',
        alternate_mobile: response.data.alternate_mobile || '',
        current_address: response.data.current_address || '',
        emergency_contact_name_1: response.data.emergency_contact_name_1 || '',
        emergency_contact_relationship_1: response.data.emergency_contact_relationship_1 || '',
        emergency_contact_phone_1: response.data.emergency_contact_phone_1 || '',
        bank_name: response.data.bank_name || '',
        bank_account_number: response.data.bank_account_number || '',
        bank_ifsc_code: response.data.bank_ifsc_code || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchOnboarding = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/my-onboarding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnboarding(response.data.process);
    } catch (error) {
      console.error('Error fetching onboarding:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/my-documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/employee-profile`,
        formData,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      alert('✅ Profile updated successfully!');
      setEditing(false);
      fetchProfile();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Verified': 'bg-green-100 text-green-800',
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Rejected': 'bg-red-100 text-red-800',
      'Completed': 'bg-green-100 text-green-800',
      'In Progress': 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const calculateOnboardingProgress = () => {
    if (!onboarding || !onboarding.tasks || onboarding.tasks.length === 0) return 0;
    const completed = onboarding.tasks.filter(t => t.status === 'Completed').length;
    return Math.round((completed / onboarding.tasks.length) * 100);
  };

  if (!profile) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-lg">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">My Profile & Self-Service</h1>
          <p className="mt-2 text-gray-600">View and update your personal information</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-6 py-3 font-semibold transition ${
                activeTab === 'profile'
                  ? 'border-b-4 border-[#2b3380] text-[#2b3380]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              👤 Profile
            </button>
            <button
              onClick={() => setActiveTab('onboarding')}
              className={`px-6 py-3 font-semibold transition ${
                activeTab === 'onboarding'
                  ? 'border-b-4 border-[#2b3380] text-[#2b3380]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              🚀 My Onboarding
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-6 py-3 font-semibold transition ${
                activeTab === 'documents'
                  ? 'border-b-4 border-[#2b3380] text-[#2b3380]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              📄 My Documents
            </button>
          </div>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Personal Information</h2>
              {!editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  ✏️ Edit Profile
                </button>
              )}
            </div>

            {editing ? (
              <form onSubmit={handleUpdateProfile}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Personal Email</label>
                    <input
                      type="email"
                      value={formData.personal_email}
                      onChange={(e) => setFormData({ ...formData, personal_email: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Alternate Mobile</label>
                    <input
                      type="tel"
                      value={formData.alternate_mobile}
                      onChange={(e) => setFormData({ ...formData, alternate_mobile: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Address</label>
                    <textarea
                      rows={2}
                      value={formData.current_address}
                      onChange={(e) => setFormData({ ...formData, current_address: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Emergency Contact Name</label>
                    <input
                      type="text"
                      value={formData.emergency_contact_name_1}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_name_1: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                    <input
                      type="text"
                      value={formData.emergency_contact_relationship_1}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_relationship_1: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                      placeholder="e.g., Spouse, Father"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Emergency Contact Phone</label>
                    <input
                      type="tel"
                      value={formData.emergency_contact_phone_1}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_phone_1: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                    <input
                      type="text"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                    <input
                      type="text"
                      value={formData.bank_account_number}
                      onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">IFSC Code</label>
                    <input
                      type="text"
                      value={formData.bank_ifsc_code}
                      onChange={(e) => setFormData({ ...formData, bank_ifsc_code: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-[#2b3380]"
                    />
                  </div>
                </div>

                <div className="mt-6 flex gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1a1f3a] transition disabled:opacity-50"
                  >
                    {loading ? '⏳ Saving...' : '💾 Save Changes'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="px-6 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition"
                  >
                    ❌ Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-gray-600">Official Email</p>
                  <p className="text-lg text-gray-900">{profile.official_email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Personal Email</p>
                  <p className="text-lg text-gray-900">{profile.personal_email || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Mobile</p>
                  <p className="text-lg text-gray-900">{profile.mobile_number}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Alternate Mobile</p>
                  <p className="text-lg text-gray-900">{profile.alternate_mobile || 'Not provided'}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm font-medium text-gray-600">Current Address</p>
                  <p className="text-lg text-gray-900">{profile.current_address}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Emergency Contact</p>
                  <p className="text-lg text-gray-900">
                    {profile.emergency_contact_name_1} ({profile.emergency_contact_relationship_1})<br />
                    {profile.emergency_contact_phone_1}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Bank Details</p>
                  <p className="text-lg text-gray-900">
                    {profile.bank_name || 'Not provided'}<br />
                    {profile.bank_account_number}<br />
                    {profile.bank_ifsc_code}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Onboarding Tab */}
        {activeTab === 'onboarding' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-6">My Onboarding Progress</h2>
            
            {!onboarding ? (
              <p className="text-gray-500">No onboarding process assigned yet.</p>
            ) : (
              <div>
                <div className="mb-6">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Overall Progress</span>
                    <span className="font-semibold">{calculateOnboardingProgress()}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div
                      className="h-4 rounded-full bg-[#2b3380] transition-all"
                      style={{ width: `${calculateOnboardingProgress()}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  {onboarding.tasks && onboarding.tasks.map((task, index) => (
                    <div key={index} className="border-2 border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{task.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                          <p className="text-xs text-gray-500 mt-2">Due: {new Date(task.due_date).toLocaleDateString()}</p>
                        </div>
                        <span className={`px-3 py-1 rounded text-xs font-semibold ${getStatusColor(task.status)}`}>
                          {task.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-6">My Documents</h2>
            
            {documents.length === 0 ? (
              <p className="text-gray-500">No documents uploaded yet.</p>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div key={doc.id} className="border-2 border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-gray-900">{doc.document_name}</h3>
                        <p className="text-sm text-gray-600">{doc.document_type}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`inline-block px-3 py-1 rounded text-xs font-semibold ${getStatusColor(doc.verification_status)}`}>
                          {doc.verification_status}
                        </span>
                        {doc.rejection_reason && (
                          <p className="text-xs text-red-600 mt-2">Reason: {doc.rejection_reason}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default EmployeeSelfService;